import { useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import type { ApiInvite } from '#/api/invite/types/ApiInvite';
import { Box } from '#/components/Box/Box';
import { CenteredContent } from '#/components/CenteredContent/CenteredContent';
import { IdealistLogo } from '#/components/Logo/IdealistLogo';
import { Text } from '#/components/Text/Text';
import { SignupFormContainer } from '#/containers/SignupFormContainer';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useIsFirstRenderRef } from '#/hooks/useIsFirstRenderRef';
import { useTrackPageView } from '#/hooks/useTrackPageView';
import { useI18n } from '#/i18n/hooks/useI18n';
import { homeRoute } from '#/routing/routes';
import { allowRedirect } from '#/utils/allowRedirect';
import { isPathOnlyUrl } from '#/utils/isPathOnlyUrl';

type Props = {
  invite: ApiInvite | null;
};

export function SignupPage({ invite }: Props) {
  const { i18n, locale } = useI18n();

  const navigate = useNavigate();

  const { user } = useUser();

  const { isFirstRenderRef } = useIsFirstRenderRef();
  useEffect(() => {
    // Redirect to home if the user lands on the login page while logged in
    if (isFirstRenderRef.current && user)
      void navigate(homeRoute.with({ lang: locale }));
  }, [locale, isFirstRenderRef, navigate, user]);

  const location = useLocation<{
    redirectTo?: string | null;
    email?: string;
    savedListing: { id: string; type: string; name: string };
    search: boolean;
  }>();

  const [searchParams] = useSearchParams();
  const savedListing = location.state?.savedListing;
  const email = location.state?.email;
  const redirectTo = searchParams.get('to') ?? location.state?.redirectTo;

  useTrackPageView('Sign Up', {
    page_type: 'Sign Up',
  });

  const redirect = () => {
    let redirectToValue = redirectTo;

    if (!redirectToValue || !allowRedirect(redirectToValue)) {
      redirectToValue = `/${locale}/`;
    }

    if (!isPathOnlyUrl(redirectToValue)) {
      window.location.assign(redirectToValue);
      return;
    }

    navigate(redirectToValue);
  };

  return (
    <CenteredContent>
      <Box textAlign="center">
        <IdealistLogo height={82} />
      </Box>

      <Text
        fontSize="24px"
        fontWeight="semiBold"
        color="mediumContentGrey"
        textAlign="center"
        mb="8px"
      >
        {
          /* TRANSLATORS: signup page title */
          i18n('Sign Up')
        }
      </Text>

      <SignupFormContainer
        analyticsTitle="[main] Signup page"
        usageContext="page"
        redirectTo={redirectTo}
        email={email}
        invite={invite}
        savedListing={savedListing}
        onSignup={redirect}
        onLogin={undefined}
        subscribedSubsite={undefined}
      />
    </CenteredContent>
  );
}
